import { useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Navbar } from "../components/Navbar";
import { Hero } from "../components/Hero";
import { WorkshopCard } from "../components/WorkshopCard";
import { GalleryMosaic } from "../components/GalleryMosaic";
import { LoungeSection } from "../components/LoungeSection";
import { Faq } from "../components/Faq";
import { PotteryPromo } from "../components/PotteryPromo";
import { Policies } from "../components/Policies";
import { Footer } from "../components/Footer";
import { BookingModal } from "../components/BookingModal";
import { MediaViewer } from "../components/MediaViewer";
import { StudioVideos } from "../components/StudioVideos";
import { api } from "../lib/api";
import { useContent } from "../lib/content";

export default function Home() {
  const { t } = useContent();
  const [workshops, setWorkshops] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [selected, setSelected] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data: ws }, { data: cats }] = await Promise.all([
        api.get("/workshops"),
        api.get("/categories"),
      ]);
      setWorkshops(ws);
      setCategories(cats);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(
    () => (filter === "All" ? workshops : workshops.filter((w) => w.theme === filter)),
    [workshops, filter]
  );

  const onBooked = useCallback(() => {
    load();
  }, [load]);

  return (
    <div>
      <Navbar />
      <Hero />

      <section id="schedule" className="relative bg-white py-20 sm:py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <div>
              <motion.p
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="font-display font-extrabold tracking-[-0.03em] text-5xl sm:text-6xl text-[#1d1d1b]"
              >
                01
              </motion.p>
              <motion.h2
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="font-display font-extrabold tracking-[-0.02em] text-3xl sm:text-4xl mt-3 leading-[1.1]"
              >
                {t("schedule_heading")}
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.15 }}
                className="font-editorial text-xl text-[#1d1d1b]/50 mt-2"
              >
                {t("schedule_kicker")}
              </motion.p>
            </div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="flex flex-wrap gap-2"
            >
              {["All", ...categories.map((c) => c.name)].map((theme) => (
                <button
                  key={theme}
                  data-testid={`workshop-filter-${theme.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                  onClick={() => setFilter(theme)}
                  className={`rounded-full px-4 py-2 text-xs font-bold tracking-wide transition-all duration-300 ${
                    filter === theme
                      ? "bg-[#1d1d1b] text-[#eae4da] shadow-[0_10px_24px_-10px_rgba(0,0,0,0.4)]"
                      : "bg-[#eae4da]/60 border border-black/[0.06] text-neutral-500 hover:text-black hover:border-black/20"
                  }`}
                >
                  {theme}
                </button>
              ))}
            </motion.div>
          </div>

          <div data-testid="workshop-grid" className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-7">
            {loading &&
              Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="rounded-[28px] border border-black/[0.05] bg-white h-[440px] animate-pulse" />
              ))}
            {!loading && filtered.length === 0 && (
              <div className="md:col-span-2 rounded-[28px] border border-dashed border-black/15 p-16 text-center">
                <p className="font-editorial text-2xl text-neutral-500">
                  Nothing on the easel for this vibe yet — check back soon.
                </p>
              </div>
            )}
            <AnimatePresence mode="popLayout">
              {filtered.map((w, i) => (
                <WorkshopCard key={w._id} workshop={w} index={i} onBook={setSelected} onView={setViewing} />
              ))}
            </AnimatePresence>
          </div>
        </div>
      </section>

      <StudioVideos />

      <GalleryMosaic />
      <LoungeSection />
      <Faq />
      <PotteryPromo />
      <Policies />
      <Footer />

      <AnimatePresence>
        {selected && (
          <BookingModal workshop={selected} onClose={() => setSelected(null)} onBooked={onBooked} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {viewing && !selected && (
          <MediaViewer
            workshop={viewing}
            onClose={() => setViewing(null)}
            onBook={(w) => { setViewing(null); setSelected(w); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
